import {Component, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {ICellEditorParams} from 'ag-grid-community';
import {Store} from '@ngrx/store';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {StockItem} from '../../../../shared-utilities/models-old/datastructures';
import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {NGPReport} from '../../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {selectCurrentNavigationPage} from '../../../../features/user-access/user-stores-store/user-store.selectors';

@Component({
  selector: 'app-stock-manager-cell-editor-multiple-barcodes',
  templateUrl: './stock-manager-cell-editor-multiple-barcodes.component.html',
  styleUrls: ['./stock-manager-cell-editor-multiple-barcodes.component.scss'],
  standalone: false,
})
export class StockManagerCellEditorMultipleBarcodesComponent implements ICellEditorAngularComp, OnDestroy {

  @ViewChild('panelAnchor', {static: true}) panelAnchor: ElementRef<HTMLElement>;

  barcodes: string[] = [''];
  showPanel = false;
  displayAbove = false;
  params: ICellEditorParams;
  colDefField: string;
  width: number;
  currentPage: string;
  readonly icons = IconsEnum;
  readonly iconColours = IconColourEnum;
  private initialBarcodes: string[] = [];
  private currentPage$: Observable<string>;
  private readonly destroy$ = new Subject<boolean>();

  constructor(
    private readonly store: Store,
  ) {
  }

  get selectedValuesLabel(): string {
    const cleaned = this.getCleanedBarcodesForDisplay();
    if (cleaned.length === 0) {
      return '';
    }
    const firstValue = cleaned[0];
    const additionalCount = cleaned.length - 1;
    return additionalCount > 0
      ? `${firstValue} + ${additionalCount} other${additionalCount > 1 ? 's' : ''}`
      : `${firstValue}`;
  }

  agInit(params: ICellEditorParams): void {
    if (Object.isFrozen(params.node.data)) {
      const mutableCopy = structuredClone(params.node.data);
      params.node.data = mutableCopy;
      params.data = mutableCopy;
    }

    this.currentPage$ = this.store.select(selectCurrentNavigationPage);
    this.currentPage$.pipe(takeUntil(this.destroy$)).subscribe((page: string) => {
      this.currentPage = page;
    });

    this.params = {...params};
    this.params.data = structuredClone(params.data);
    this.colDefField = params.colDef.field;
    this.ensureOriginalValueEntry();
    if (!Array.isArray(this.params.data._tags)) {
      this.params.data._tags = [];
    }

    this.initialBarcodes = this.normalizeBarcodes(this.params.data[this.colDefField]);
    this.barcodes = this.initialBarcodes.length > 0 ? [...this.initialBarcodes] : [''];
    this.width = this.params.colDef.width;
    params.api.setColumnWidths([
      {key: params.colDef.field, newWidth: 220},
    ]);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  afterGuiAttached(): void {
    this.openPanel();
  }

  openPanel(): void {
    this.showPanel = true;
    setTimeout(() => {
      this.adjustPanelPosition();
    }, 0);
  }

  getValue(): string[] {
    const cleaned = this.getCleanedBarcodes();
    this.ensureOriginalValueEntry();
    this.params.data[this.colDefField] = cleaned;
    this.params.node.data[this.colDefField] = cleaned;

    GridUtils.updateSingleItem(
      this.params.data as StockItem | NGPReport,
      this.colDefField,
      cleaned,
      this.store,
      this.currentPage ?? 'stock-manager',
    );
    GridUtils.updateIsEditedAndForceRefresh(this.params);

    return cleaned;
  }

  refresh(): boolean {
    return false;
  }

  onBarcodeInput(index: number, value: string): void {
    this.barcodes = this.barcodes.map((barcode: string, i: number) => i === index ? value : barcode);
  }

  onBarcodeInputEvent(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.onBarcodeInput(index, target.value);
  }

  onPanelPointerEvent(event: MouseEvent): void {
    event.stopPropagation();
  }

  onAddBarcodeClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.canAddBarcode()) {
      return;
    }
    this.barcodes = [...this.barcodes, ''];
    setTimeout((): void => {
      this.focusLastBarcodeInput();
    }, 0);
  }

  onRemoveBarcodeClick(index: number, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.onRemoveBarcode(index);
  }

  onRemoveBarcode(index: number): void {
    if (this.barcodes.length <= 1) {
      this.barcodes = [''];
      return;
    }
    this.barcodes = this.barcodes.filter((_: string, i: number) => i !== index);
  }

  canAddBarcode(): boolean {
    const lastBarcode = this.barcodes[this.barcodes.length - 1];
    return String(lastBarcode ?? '').trim().length > 0;
  }

  getEntryLabel(index: number): string {
    return `Barcode ${index + 1}`;
  }

  onConfirmClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    const cleaned = this.getCleanedBarcodes();
    this.ensureOriginalValueEntry();
    this.params.data[this.colDefField] = cleaned;
    this.params.node.data[this.colDefField] = cleaned;
    this.showPanel = false;
    this.params.api.stopEditing();
  }

  onCancelClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.barcodes = this.initialBarcodes.length > 0 ? [...this.initialBarcodes] : [''];
    this.showPanel = false;
    this.params.api.stopEditing(true);
  }

  onUndoChangesClick(): void {
    const original = this.normalizeBarcodes(this.params.data.originalValue[this.colDefField].value);
    this.barcodes = original.length > 0 ? [...original] : [''];
    this.params.data[this.colDefField] = original;
    this.params.node.data[this.colDefField] = original;
    GridUtils.removeIsEditingFlagAndDispatch(
      this.params.data as StockItem | NGPReport,
      this.colDefField,
      this.store,
      this.currentPage ?? 'stock-manager',
    );
    GridUtils.updateIsEditedAndForceRefresh(this.params);
  }

  hasUndoChanges(): boolean {
    return !this.arraysAreEqual(this.getCleanedBarcodes(), this.initialBarcodes);
  }

  trackByIndex(index: number): number {
    return index;
  }

  getCleanedBarcodesForDisplay(): string[] {
    return this.getCleanedBarcodes();
  }

  formatSelectedValues(values: string[]): string {
    return values.map((value: string) => `${value}`).join(', \n');
  }

  private ensureOriginalValueEntry(): void {
    if (!this.params?.data?.originalValue) {
      return;
    }
    const field = this.colDefField;
    const normalizedCurrent = this.normalizeBarcodes(this.params.data[field]);
    const existing = this.params.data.originalValue[field];
    if (!existing) {
      this.params.data.originalValue[field] = {value: normalizedCurrent};
      return;
    }
    existing.value = this.normalizeBarcodes(existing.value);
  }

  private focusLastBarcodeInput(): void {
    const inputs = this.panelAnchor?.nativeElement?.querySelectorAll(
      '.stock-manager-multiple-barcodes-editor-input',
    );
    if (!inputs?.length) {
      return;
    }
    const lastInput = inputs[inputs.length - 1] as HTMLInputElement;
    lastInput.focus();
  }

  private adjustPanelPosition(): void {
    const anchor = this.panelAnchor?.nativeElement;
    if (!anchor) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const panelHeight = 280;
    this.displayAbove = rect.bottom + panelHeight > window.innerHeight;
  }

  private getCleanedBarcodes(): string[] {
    return this.barcodes
      .map((barcode: string) => barcode.trim())
      .filter((barcode: string) => barcode.length > 0);
  }

  private normalizeBarcodes(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value
        .map((barcode: string | number) => String(barcode).trim())
        .filter((barcode: string) => barcode.length > 0);
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      return value
        .split(',')
        .map((barcode: string) => barcode.trim())
        .filter((barcode: string) => barcode.length > 0);
    }
    return [];
  }

  private arraysAreEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }
    return arr1.every((value: string, index: number) => value === arr2[index]);
  }

}
